import { ConnectError } from '@bufbuild/connect';
import {
  DocumentViewer,
  LinkButton,
  deserify,
  getMediaBasePath,
  useCommonServiceClientContext,
  useFeatureEnabled,
  ReportButton,
} from '@geneo2-web/shared-ui';
import { Box } from '@mui/material';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { Feature } from '@protos/school_management/school.db_pb';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../Auth/auth.events';

interface IViewReport {
  homeworkId: number;
  reportButtonHandler?: () => void;
  attemptNumber?: number;
  subject?: string;
  className?: string;
  section?: string;
}

export default function ViewReportButton(props: IViewReport) {
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const { SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const user = deserify(useAppSelector((state) => state.auth.user_info));
  const featureInfo = user?.schoolDetails[0]?.featuresPlanInfo;
  const isViewReportsEnabled = useFeatureEnabled(
    featureInfo,
    Feature.VIEW_REPORTS
  );
  const { setShowAppLoader } = useGlobalContext();
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const dispatch = useDispatch();

  // ------------------------------------------------------------

  const fetchSchoolAssessmentReport = async () => {
    try {
      setShowAppLoader(true);
      const res =
        await SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler.fetchSchoolAssessmentReport(
          {
            assessmentId: props.homeworkId,
            teacherId: user?.teacherProfileId,
            attemptNumber: props.attemptNumber,
          }
        );
      if (res.data?.gcpReportUrl) {
        setPdfUrl(getMediaBasePath(res.data.gcpReportUrl));
      } else {
        dispatch(
          setToastInfo({
            label: res.message,
            variant: 'success',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    } catch (err) {
      if (err instanceof ConnectError) {
        console.log('Error:', err.rawMessage);
        if (
          err.rawMessage.includes(
            'No completed student submissions found for the assessment'
          )
        ) {
          dispatch(
            setToastInfo({
              label: 'No Student has submitted the assessment.',
              variant: 'error',
              open: true,
            })
          );
        } else {
          dispatch(
            setToastInfo({
              label: err.rawMessage,
              variant: 'error',
              open: true,
            })
          );
        }
      } else {
        dispatch(
          setToastInfo({
            label: 'Error while generating report',
            variant: 'error',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    }
  };

  // ------------------------------------------------------------

  if (!isViewReportsEnabled) {
    return null;
  }
  return (
    <>
      <Box
        sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}
      >
        <ReportButton
          buttonText="Teacher Report"
          buttonList={[
            {
              name: 'Teacher Report',
              onClick: async () => {
                props.reportButtonHandler
                  ? props.reportButtonHandler()
                  : fetchSchoolAssessmentReport();
                await interactionEvent({
                  context: 'View_Assessment',
                  name: 'VIEW_REPORT',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              },
            },
          ]}
        />
      </Box>

      {pdfUrl && (
        <DocumentViewer
          downloadUrl={pdfUrl}
          previewUrl={pdfUrl}
          gcpUrl={pdfUrl}
          type={FileEnum.FILE_TYPE_PDF}
          fileName={
            props.subject || props.className || props.section
              ? `${props.subject || ''}_${props.className || ''}_${
                  props.section || ''
                }_Geneo_Assessment_Report_Teacher_${props.homeworkId}.pdf`
              : `Geneo_Assessment_Report_Teacher_${props.homeworkId}.pdf`
          }
          open={!!pdfUrl}
          closeFile={() => {
            setPdfUrl(undefined);
          }}
        />
      )}
    </>
  );
}
